import { OverlayContainer } from '@angular/cdk/overlay';
import { ApplicationRef, createComponent, EnvironmentInjector, inject, Type } from '@angular/core';

import {
  TellentMobileSheetHandle,
  TellentMobileSheetOptions,
  TellentMobileSheetPresenter,
} from './sheet-presenter.tokens';

export interface IonModalSheetElement extends HTMLElement {
  breakpoints: number[] | undefined;
  initialBreakpoint: number | undefined;
  canDismiss: boolean;
  handle: boolean | undefined;
  delegate: {
    attachViewToDom: (...args: unknown[]) => Promise<HTMLElement | null>;
    removeViewFromDom: (...args: unknown[]) => Promise<void>;
  };
  setCurrentBreakpoint: (breakpoint: number) => Promise<void>;
  present: () => Promise<void>;
  dismiss: (data?: unknown, role?: string) => Promise<boolean>;
  onWillDismiss: <T = unknown>() => Promise<{ data?: T; role?: string }>;
  onDidDismiss: <T = unknown>() => Promise<{ data?: T; role?: string }>;
}

type SheetBackground = NonNullable<TellentMobileSheetOptions['background']>;

export interface IonModalSheetPresenterOptions {
  // Present as a centered modal dialog instead of a full-height bottom sheet
  // (recruitee-mobile does this on tablets).
  presentAsDialog?: () => Promise<boolean> | boolean;
  // Host theme class for the sheet chrome. When omitted, the chrome is set
  // inline from design-system tokens instead.
  sheetCssClass?: (background: SheetBackground) => string;
}

interface SheetPresenterDeps {
  appRef: ApplicationRef;
  environmentInjector: EnvironmentInjector;
  overlayContainer: OverlayContainer;
}

const SHEET_BACKGROUNDS: Record<SheetBackground, string> = {
  default: 'var(--color-bg-default)',
  neutral: 'var(--color-bg-neutral-default)',
};

// The sheet is deliberately NOT presented through the Ionic ModalController:
// modals attach to ion-app, whose containment traps their z-index below the
// body-level CDK overlay container, so a sheet opened over a CDK overlay
// (e.g. the assistant thread drawer) would render underneath it. Mounting the
// bare ion-modal inside the overlay container (in tellent-client that is the
// shadow-root container carrying the design-system tokens and adopted styles)
// puts it in the same stacking context as those overlays; Ionic's own z-index
// (20000 + overlay index) then orders it above them and above
// earlier-presented modals — do not override it.
async function createIonModalSheet(
  deps: SheetPresenterDeps,
  presenterOptions: IonModalSheetPresenterOptions,
  options: TellentMobileSheetOptions,
): Promise<TellentMobileSheetHandle> {
  // Lazily pull in the bare ion-modal web component; desktop bundles never
  // load it. defineCustomElement is a no-op when the host app (Ionic Angular
  // or Ionic React) already registered the element.
  const { defineCustomElement } = await import('@ionic/core/components/ion-modal.js');
  defineCustomElement();

  const componentRef = createComponent(<Type<unknown>>options.component, {
    environmentInjector: deps.environmentInjector,
  });
  Object.entries(options.componentProps ?? {}).forEach(([key, value]) =>
    componentRef.setInput(key, value),
  );

  const background = options.background ?? 'default';
  const modal = <IonModalSheetElement>document.createElement('ion-modal');
  if (presenterOptions.sheetCssClass) {
    modal.classList.add(presenterOptions.sheetCssClass(background));
  } else {
    modal.style.setProperty('--height', 'auto');
    modal.style.setProperty('--background', SHEET_BACKGROUNDS[background]);
  }
  modal.breakpoints = [0, 1];
  modal.initialBreakpoint = (await presenterOptions.presentAsDialog?.()) ? undefined : 1;
  modal.canDismiss = options.canDismiss ?? true;
  // The CDK overlay container disables pointer events for its children.
  modal.style.pointerEvents = 'auto';
  modal.appendChild(<HTMLElement>componentRef.location.nativeElement);
  // Ionic "teleports" inline modals into ion-app on present (CoreDelegate),
  // which would trap the sheet inside ion-app's stacking context, underneath
  // the overlay container and its overlays. A no-op delegate keeps the modal
  // mounted where we attached it.
  modal.delegate = {
    attachViewToDom: () => Promise.resolve(<HTMLElement | null>modal.firstElementChild),
    removeViewFromDom: () => Promise.resolve(),
  };

  deps.overlayContainer.getContainerElement().appendChild(modal);
  deps.appRef.attachView(componentRef.hostView);

  void modal.onDidDismiss().then(() => {
    deps.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
    modal.remove();
  });

  return {
    present: () => modal.present(),
    dismiss: (data?: unknown, role?: string) => modal.dismiss(data, role),
    onWillDismiss: () => modal.onWillDismiss(),
  };
}

/** Must be called in an injection context (e.g. a provider factory). */
export function createIonModalSheetPresenter(
  presenterOptions: IonModalSheetPresenterOptions = {},
): TellentMobileSheetPresenter {
  const deps: SheetPresenterDeps = {
    appRef: inject(ApplicationRef),
    environmentInjector: inject(EnvironmentInjector),
    overlayContainer: inject(OverlayContainer),
  };
  return {
    createSheet: options => createIonModalSheet(deps, presenterOptions, options),
  };
}
